// Co-located dictionary for CaseStudies.tsx (issue #38).
// Stays literal in the component per style guide §2 rules 1-2: the
// SUCCESS_PROTOCOLS hero badge and the SCHEDULE_CONSULTATION() CTA
// (German context sits in the same viewport). SEOHead props and the
// ItemList JSON-LD stay EN until the SEO issue (#48) localizes meta.
// Quotes carry their own quotation marks so DE renders real „…“.

interface CaseStudy {
  id: string;
  title: string;
  industry: string;
  challenge: string;
  metrics: { value: string; label: string }[];
  solution: string;
  results: string[];
  quote: string;
  author: string;
  detailLink?: string;
}

type CaseStudiesDict = {
  heroEyebrow: string;
  heroTitle: string;
  heroSubtitle: string;
  detailedTag: string;
  readFullCaseStudy: string;
  discussSimilarProject: string;
  ctaTitle: string;
  ctaBody: string;
  caseStudies: CaseStudy[];
  seo: {
    title: string;
    description: string;
    keywords: string;
    listName: string;
    listDescription: string;
  };
};

const en: CaseStudiesDict = {
  heroEyebrow: "CASE_STUDIES",
  heroTitle: "Automation Case Studies",
  heroSubtitle: "Real Results from Real Automation",
  detailedTag: "Detailed",
  readFullCaseStudy: "Read Full Case Study",
  discussSimilarProject: "Discuss Similar Project",
  ctaTitle: "YOUR_STORY_NEXT?",
  ctaBody:
    "Every case study started with a conversation. Let's talk about what automation can do for your business.",
  caseStudies: [
    {
      id: "construction-automation",
      title: "Construction Project Management Automation",
      industry: "Construction",
      challenge:
        "An Arizona construction firm managing $12M+ in annual projects was stuck between spreadsheets and Podio. Over $400K in billing discrepancies went unnoticed while invoices went out weeks late.",
      metrics: [
        { value: "26 hrs/wk", label: "Recovered" },
        { value: "$400K+", label: "Billing errors captured" },
        { value: "$120K/yr", label: "Saved" },
      ],
      solution:
        "Custom intelligent platform built on Replit, replacing the patchwork of spreadsheets and Podio with real-time dashboards, automated invoicing, and field update capture.",
      results: [
        "26 hours/week of manual data entry eliminated",
        "$400K+ in billing errors captured and recovered",
        "$120K/year saved in administrative overhead",
      ],
      quote:
        "\"Before this system, I spent half my week just figuring out where projects stood.\"",
      author: "Operations Director",
      detailLink: "/case-studies/construction-automation",
    },
    {
      id: "manufacturing-operations",
      title: "cGMP Manufacturing Order Automation",
      industry: "Manufacturing",
      challenge:
        "A biotech manufacturer operating under cGMP protocols had manual order entry into three separate systems. Each entry was an opportunity for compliance-threatening error.",
      metrics: [
        { value: "32 hrs/wk", label: "Eliminated" },
        { value: "99.2%", label: "Order accuracy" },
        { value: "$68K/yr", label: "Saved" },
      ],
      solution:
        "Podio as single source of truth with automated validation, production scheduling, batch record generation, and audit trails.",
      results: [
        "Order processing dropped from 4.5 days to 12 hours",
        "Zero compliance violations since implementation",
        "Now competing on 24-hour turnaround as a differentiator",
      ],
      quote:
        "\"Our team can finally focus on manufacturing instead of administration.\"",
      author: "Quality & Operations Manager",
      detailLink: "/case-studies/manufacturing-operations",
    },
    {
      id: "farm-automation",
      title: "Farm Cuts Labor Costs 85%",
      industry: "Agriculture",
      challenge:
        "Six employees spent 40 hours every two weeks manually creating labels for 5,000 plants. This single task cost $20,800 annually before counting errors and delays.",
      metrics: [
        { value: "85%", label: "Cost reduction" },
        { value: "240 hrs", label: "Saved monthly" },
        { value: "Zero", label: "Labeling errors" },
      ],
      solution:
        "Integrated existing inventory management system with automated label generation. Labels print automatically when plants enter the system.",
      results: [
        "85% reduction in labeling costs",
        "240 hours saved per month",
        "Staff redirected to revenue activities",
      ],
      quote:
        "\"We went from six people doing labels to six people growing our business.\"",
      author: "Farm Operations Manager",
    },
    {
      id: "spa-scheduling",
      title: "Spa Increases Revenue 30%",
      industry: "Hospitality",
      challenge:
        "Understaffed spa struggled with scheduling gaps, leading to lost appointments and overworked employees. Manual scheduling limited capacity and flexibility.",
      metrics: [
        { value: "30%", label: "Revenue increase" },
        { value: "50%", label: "Staffing boost" },
        { value: "Zero", label: "Scheduling conflicts" },
      ],
      solution:
        "Dynamic scheduling system that optimizes staff allocation and automates coverage gaps, maximizing both utilization and satisfaction.",
      results: [
        "30% increase in revenue",
        "50% boost in effective staffing",
        "Improved work-life balance for staff",
      ],
      quote:
        "\"The system doesn't just schedule -- it optimizes. We're serving more clients.\"",
      author: "Spa Owner",
    },
    {
      id: "solar-support",
      title: "Solar Support to Revenue Stream",
      industry: "Energy",
      challenge:
        "Customer service calls overwhelmed staff, created bottlenecks, and provided zero revenue opportunity. Critical support tickets got lost or misdirected.",
      metrics: [
        { value: "15%", label: "New revenue stream" },
        { value: "100%", label: "Routing accuracy" },
        { value: "Instant", label: "Acknowledgment" },
      ],
      solution:
        "Intelligent voice agent that captures calls, generates support tickets via text, routes issues correctly, and identifies upsell opportunities.",
      results: [
        "15% new revenue stream from support interactions",
        "100% ticket routing accuracy",
        "Support team focused on solutions, not triage",
      ],
      quote:
        "\"Our support system went from cost center to profit center.\"",
      author: "Solar Company CEO",
    },
    {
      id: "contractor-scaling",
      title: "Contractor Scales Sales 80%",
      industry: "Construction",
      challenge:
        "Complex sales rep onboarding took weeks and limited growth. New reps struggled with processes, reducing effectiveness and increasing turnover.",
      metrics: [
        { value: "80%", label: "Sales team growth" },
        { value: "50%", label: "More qualified leads" },
        { value: "5 days", label: "Onboarding time" },
      ],
      solution:
        "Streamlined and automated the entire onboarding workflow with a self-guided system that gets new reps productive in days, not weeks.",
      results: [
        "80% increase in sales team size",
        "30% improvement in closure rates",
        "Onboarding reduced to 5 days",
      ],
      quote:
        "\"We went from struggling to hire to struggling to keep up with growth.\"",
      author: "General Contractor Owner",
    },
    {
      id: "global-manufacturer",
      title: "Global Inventory Portal",
      industry: "Manufacturing",
      challenge:
        "Global manufacturer with offices in the Netherlands, Britain, and the US struggled with inventory tracking across locations. Administrative overhead created inefficiencies.",
      metrics: [
        { value: "30%", label: "Admin cost reduction" },
        { value: "15%", label: "Sales capacity increase" },
        { value: "Real-time", label: "Global visibility" },
      ],
      solution:
        "Unified portal to track inventory movement between all three locations with real-time visibility and automated reporting.",
      results: [
        "30% reduction in admin costs",
        "15% increase in sales capacity",
        "Eliminated manual tracking across locations",
      ],
      quote:
        "\"We can finally see everything in real-time and focus on selling.\"",
      author: "Global Operations Director",
    },
    {
      id: "resort-spa-covid",
      title: "Resort Spa 100% Compliance",
      industry: "Hospitality",
      challenge:
        "During COVID, a resort spa needed a HIPAA-compliant tracking system for testing and results to ensure guest safety and meet strict regulations.",
      metrics: [
        { value: "100%", label: "Compliance" },
        { value: "HIPAA", label: "Data protection" },
        { value: "1 month", label: "Implementation" },
      ],
      solution:
        "Comprehensive HIPAA-compliant tracking tool within one month that automated COVID test scheduling, result management, and regulatory reporting.",
      results: [
        "100% compliance with regulations",
        "HIPAA-compliant data protection",
        "Automated scheduling and tracking",
      ],
      quote:
        "\"The system gave us complete confidence and allowed us to focus on safety.\"",
      author: "Resort Spa Manager",
    },
  ],
  seo: {
    title: "Case Studies - Real Automation Results | Fitted Automation",
    description: "See how businesses across construction, manufacturing, agriculture, and hospitality saved thousands of hours and dollars with custom automation solutions.",
    keywords: "automation case studies, business automation results, construction automation, manufacturing automation, ROI automation",
    listName: "Fitted Automation Case Studies",
    listDescription: "Real-world automation success stories across construction, manufacturing, agriculture, hospitality, and energy industries.",
  },
};

const de: typeof en = {
  heroEyebrow: "CASE_STUDIES",
  heroTitle: "Automatisierungs-Fallstudien",
  heroSubtitle: "Echte Ergebnisse durch echte Automatisierung",
  detailedTag: "Details",
  readFullCaseStudy: "Komplette Fallstudie lesen",
  discussSimilarProject: "Ähnliches Projekt besprechen",
  ctaTitle: "FOLGT_IHRE_STORY?",
  ctaBody:
    "Jede Fallstudie hat mit einem Gespräch begonnen. Sprechen wir darüber, was Automatisierung für Ihr Unternehmen leisten kann.",
  caseStudies: [
    {
      id: "construction-automation",
      title: "Automatisiertes Projektmanagement im Baugewerbe",
      industry: "Baugewerbe",
      challenge:
        "Ein Bauunternehmen aus Arizona mit einem jährlichen Projektvolumen von über $12M steckte zwischen Tabellenkalkulationen und Podio fest. Abrechnungsdifferenzen von über $400K blieben unbemerkt, während Rechnungen mit wochenlanger Verspätung verschickt wurden.",
      metrics: [
        { value: "26 Std./Wo.", label: "Gewonnen" },
        { value: "$400K+", label: "Abrechnungsfehler aufgedeckt" },
        { value: "$120K/Jahr", label: "Eingespart" },
      ],
      solution:
        "Maßgeschneiderte intelligente Plattform auf Replit-Basis, die den Flickenteppich aus Tabellen und Podio durch Echtzeit-Dashboards, automatisierte Rechnungsstellung und die Erfassung von Baustellen-Updates ersetzt.",
      results: [
        "26 Stunden manueller Dateneingabe pro Woche eliminiert",
        "Über $400K an Abrechnungsfehlern aufgedeckt und zurückgeholt",
        "$120K Verwaltungskosten pro Jahr eingespart",
      ],
      quote:
        "„Vor diesem System habe ich die halbe Woche nur damit verbracht, herauszufinden, wo unsere Projekte gerade stehen.“",
      author: "Betriebsleiter",
      detailLink: "/case-studies/construction-automation",
    },
    {
      id: "manufacturing-operations",
      title: "Auftragsautomatisierung in der cGMP-Fertigung",
      industry: "Fertigung",
      challenge:
        "Ein Biotech-Hersteller, der nach cGMP-Vorgaben produziert, erfasste Aufträge manuell in drei getrennten Systemen. Jede einzelne Eingabe war eine potenzielle Fehlerquelle – und damit ein Compliance-Risiko.",
      metrics: [
        { value: "32 Std./Wo.", label: "Eliminiert" },
        { value: "99,2%", label: "Auftragsgenauigkeit" },
        { value: "$68K/Jahr", label: "Eingespart" },
      ],
      solution:
        "Podio als zentrale Datenquelle – mit automatisierter Validierung, Produktionsplanung, Chargenprotokollen und Audit-Trails.",
      results: [
        "Auftragsbearbeitung von 4,5 Tagen auf 12 Stunden verkürzt",
        "Null Compliance-Verstöße seit der Einführung",
        "24-Stunden-Durchlaufzeit ist heute ein echter Wettbewerbsvorteil",
      ],
      quote:
        "„Unser Team kann sich endlich auf die Fertigung konzentrieren statt auf Verwaltung.“",
      author: "Leiter Qualität & Betrieb",
      detailLink: "/case-studies/manufacturing-operations",
    },
    {
      id: "farm-automation",
      title: "Farm senkt Personalkosten um 85%",
      industry: "Landwirtschaft",
      challenge:
        "Sechs Mitarbeiter verbrachten alle zwei Wochen 40 Stunden damit, Etiketten für 5.000 Pflanzen von Hand zu erstellen. Allein diese Aufgabe kostete $20,800 pro Jahr – Fehler und Verzögerungen nicht eingerechnet.",
      metrics: [
        { value: "85%", label: "Kostensenkung" },
        { value: "240 Std.", label: "Monatlich gespart" },
        { value: "Null", label: "Etikettierfehler" },
      ],
      solution:
        "Anbindung der bestehenden Warenwirtschaft an eine automatische Etikettenerstellung. Etiketten werden gedruckt, sobald Pflanzen im System erfasst sind.",
      results: [
        "85% geringere Etikettierkosten",
        "240 eingesparte Stunden pro Monat",
        "Mitarbeiter arbeiten jetzt an umsatzwirksamen Aufgaben",
      ],
      quote:
        "„Aus sechs Leuten, die Etiketten erstellt haben, wurden sechs Leute, die unser Geschäft voranbringen.“",
      author: "Farm-Betriebsleiter",
    },
    {
      id: "spa-scheduling",
      title: "Spa steigert Umsatz um 30%",
      industry: "Gastgewerbe",
      challenge:
        "Ein unterbesetztes Spa kämpfte mit Lücken im Dienstplan – verlorene Termine und überlastete Mitarbeiter waren die Folge. Die manuelle Planung begrenzte Kapazität und Flexibilität.",
      metrics: [
        { value: "30%", label: "Umsatzsteigerung" },
        { value: "50%", label: "Kapazitätszuwachs" },
        { value: "Null", label: "Planungskonflikte" },
      ],
      solution:
        "Ein dynamisches Planungssystem optimiert den Personaleinsatz und schließt Lücken automatisch – für maximale Auslastung und Zufriedenheit.",
      results: [
        "30% mehr Umsatz",
        "50% mehr effektive Personalkapazität",
        "Bessere Work-Life-Balance für das Team",
      ],
      quote:
        "„Das System plant nicht nur – es optimiert. Wir betreuen heute mehr Kunden.“",
      author: "Spa-Inhaber",
    },
    {
      id: "solar-support",
      title: "Solar-Support wird zur Umsatzquelle",
      industry: "Energie",
      challenge:
        "Kundenservice-Anrufe überlasteten das Team, erzeugten Engpässe und brachten keinerlei Umsatzchancen. Kritische Support-Tickets gingen verloren oder wurden falsch zugeordnet.",
      metrics: [
        { value: "15%", label: "Neue Umsatzquelle" },
        { value: "100%", label: "Routing-Genauigkeit" },
        { value: "Sofort", label: "Bestätigung" },
      ],
      solution:
        "Intelligenter Sprachagent, der Anrufe entgegennimmt, Support-Tickets per Text erstellt, Anliegen korrekt weiterleitet und Upselling-Potenziale erkennt.",
      results: [
        "15% zusätzlicher Umsatz aus Support-Interaktionen",
        "100% korrekt zugeordnete Tickets",
        "Support-Team konzentriert sich auf Lösungen statt auf Vorsortierung",
      ],
      quote:
        "„Unser Support wurde von der Kostenstelle zum Profitcenter.“",
      author: "CEO, Solarunternehmen",
    },
    {
      id: "contractor-scaling",
      title: "Vertriebsteam wächst um 80%",
      industry: "Baugewerbe",
      challenge:
        "Das komplexe Onboarding neuer Vertriebsmitarbeiter dauerte Wochen und bremste das Wachstum. Neue Mitarbeiter kämpften mit den Prozessen – das minderte ihre Leistung und erhöhte die Fluktuation.",
      metrics: [
        { value: "80%", label: "Vertriebsteam-Wachstum" },
        { value: "50%", label: "Mehr qualifizierte Leads" },
        { value: "5 Tage", label: "Onboarding-Zeit" },
      ],
      solution:
        "Der komplette Onboarding-Workflow wurde verschlankt und automatisiert: Ein selbsterklärendes System macht neue Vertriebsmitarbeiter in Tagen statt Wochen produktiv.",
      results: [
        "Vertriebsteam um 80% vergrößert",
        "Abschlussquote um 30% verbessert",
        "Onboarding auf 5 Tage verkürzt",
      ],
      quote:
        "„Früher hatten wir Mühe, Leute einzustellen – heute haben wir Mühe, mit dem Wachstum Schritt zu halten.“",
      author: "Inhaber, Generalunternehmen",
    },
    {
      id: "global-manufacturer",
      title: "Globales Bestandsportal",
      industry: "Fertigung",
      challenge:
        "Ein weltweit tätiger Hersteller mit Standorten in den Niederlanden, Großbritannien und den USA kämpfte mit der Bestandsverfolgung über alle Standorte hinweg. Der Verwaltungsaufwand führte zu Ineffizienzen.",
      metrics: [
        { value: "30%", label: "Weniger Verwaltungskosten" },
        { value: "15%", label: "Mehr Vertriebskapazität" },
        { value: "Echtzeit", label: "Globale Transparenz" },
      ],
      solution:
        "Ein einheitliches Portal verfolgt Bestandsbewegungen zwischen allen drei Standorten – mit Echtzeit-Transparenz und automatisiertem Reporting.",
      results: [
        "30% geringere Verwaltungskosten",
        "15% mehr Vertriebskapazität",
        "Manuelle Nachverfolgung über alle Standorte hinweg abgeschafft",
      ],
      quote:
        "„Wir sehen endlich alles in Echtzeit und können uns aufs Verkaufen konzentrieren.“",
      author: "Internationaler Betriebsleiter",
    },
    {
      id: "resort-spa-covid",
      title: "Resort-Spa: 100% Compliance",
      industry: "Gastgewerbe",
      challenge:
        "Während der COVID-Pandemie benötigte ein Resort-Spa ein HIPAA-konformes System zur Erfassung von Tests und Ergebnissen, um die Sicherheit der Gäste zu gewährleisten und strenge Auflagen zu erfüllen.",
      metrics: [
        { value: "100%", label: "Compliance" },
        { value: "HIPAA", label: "Datenschutz" },
        { value: "1 Monat", label: "Implementierung" },
      ],
      solution:
        "Umfassendes HIPAA-konformes Tracking-Tool, umgesetzt innerhalb eines Monats – mit automatisierter COVID-Testplanung, Ergebnisverwaltung und regulatorischem Reporting.",
      results: [
        "100% Einhaltung aller Auflagen",
        "HIPAA-konformer Datenschutz",
        "Automatisierte Terminplanung und Nachverfolgung",
      ],
      quote:
        "„Das System gab uns absolute Sicherheit – wir konnten uns voll auf den Schutz unserer Gäste konzentrieren.“",
      author: "Resort-Spa-Manager",
    },
  ],
  seo: {
    title: "Fallstudien: Automatisierungsergebnisse | Fitted",
    description: "So haben Unternehmen aus Baugewerbe, Fertigung, Landwirtschaft und Gastgewerbe mit individueller Automatisierung Tausende Stunden und Dollar gespart.",
    keywords: "Automatisierung Fallstudien, Ergebnisse Geschäftsautomatisierung, Automatisierung Baugewerbe, Automatisierung Fertigung, ROI Automatisierung",
    listName: "Fitted Automation Fallstudien",
    listDescription: "Echte Automatisierungserfolge aus Baugewerbe, Fertigung, Landwirtschaft, Gastgewerbe und Energiewirtschaft.",
  },
};

export const caseStudiesI18n = { en, de };
